import {
  ClientRequest,
  ClientRequestList,
  RequestAnswer,
  RequestItemInput,
  RequestLinkInfo,
  RequestRecipients,
  RequestSave,
  RequestSendResult,
  RequestSuggestion,
  RequestTemplate,
} from "../features/clientRequests/clientRequests.types";
import { clientPortalSlice } from "./ClientPortalSlice";
import { portalSlice } from "./PortalSlice";

/*
 * Client request lists (Mg-232). Firm side (`api/client-requests`) rides on the client portal's
 * firm API; the client side (`api/portal/requests`) on the portal's own API (portal token). Injected,
 * so no new store slice is needed.
 */

const firmApi = clientPortalSlice.enhanceEndpoints({
  addTagTypes: ["ClientRequests", "ClientRequestTemplates"],
});

export const clientRequestsApi = firmApi.injectEndpoints({
  endpoints: (build) => ({
    getWorkClientRequests: build.query<ClientRequestList, { workId: string }>({
      query: ({ workId }) => `api/client-requests/work/${workId}`,
      providesTags: ["ClientRequests"],
    }),
    getClientRequestRecipients: build.query<
      RequestRecipients,
      { workId: string }
    >({
      query: ({ workId }) => `api/client-requests/work/${workId}/recipients`,
    }),
    getClientRequestSuggestion: build.query<
      RequestSuggestion | null,
      { workId: string }
    >({
      query: ({ workId }) => ({
        url: `api/client-requests/work/${workId}/suggest`,
        // 204 = nothing to suggest.
        responseHandler: async (response) =>
          response.status === 204 ? null : response.json(),
      }),
      providesTags: ["ClientRequests"],
    }),
    createClientRequest: build.mutation<RequestSendResult, RequestSave>({
      query: (body) => ({ url: "api/client-requests", method: "POST", body }),
      invalidatesTags: ["ClientRequests"],
    }),
    updateClientRequest: build.mutation<
      RequestSendResult,
      { id: string; body: RequestSave }
    >({
      query: ({ id, body }) => ({
        url: `api/client-requests/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ClientRequests"],
    }),
    sendClientRequest: build.mutation<RequestSendResult, string>({
      query: (id) => ({
        url: `api/client-requests/${id}/send`,
        method: "POST",
      }),
      invalidatesTags: ["ClientRequests"],
    }),
    remindClientRequest: build.mutation<RequestSendResult, string>({
      query: (id) => ({
        url: `api/client-requests/${id}/remind`,
        method: "POST",
      }),
      invalidatesTags: ["ClientRequests"],
    }),
    cancelClientRequest: build.mutation<ClientRequest, string>({
      query: (id) => ({
        url: `api/client-requests/${id}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["ClientRequests"],
    }),
    deleteClientRequest: build.mutation<boolean, string>({
      query: (id) => ({ url: `api/client-requests/${id}`, method: "DELETE" }),
      invalidatesTags: ["ClientRequests"],
    }),
    getClientRequestTemplates: build.query<
      { available: boolean; templates: RequestTemplate[] },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: "api/client-requests/templates",
        params: { orgId },
      }),
      providesTags: ["ClientRequestTemplates"],
    }),
    saveClientRequestTemplate: build.mutation<
      RequestTemplate,
      { orgId: string; name: string; items: RequestItemInput[] }
    >({
      query: (body) => ({
        url: "api/client-requests/templates",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ClientRequestTemplates"],
    }),
    deleteClientRequestTemplate: build.mutation<
      boolean,
      { id: string; orgId: string }
    >({
      query: ({ id, orgId }) => ({
        url: `api/client-requests/templates/${id}`,
        method: "DELETE",
        params: { orgId },
      }),
      invalidatesTags: ["ClientRequestTemplates"],
    }),
  }),
});

export const {
  useGetWorkClientRequestsQuery,
  useGetClientRequestRecipientsQuery,
  useGetClientRequestSuggestionQuery,
  useCreateClientRequestMutation,
  useUpdateClientRequestMutation,
  useSendClientRequestMutation,
  useRemindClientRequestMutation,
  useCancelClientRequestMutation,
  useDeleteClientRequestMutation,
  useGetClientRequestTemplatesQuery,
  useSaveClientRequestTemplateMutation,
  useDeleteClientRequestTemplateMutation,
} = clientRequestsApi;

const portalApi = portalSlice.enhanceEndpoints({
  addTagTypes: ["PortalRequests"],
});

export const portalRequestsApi = portalApi.injectEndpoints({
  endpoints: (build) => ({
    getRequestLink: build.query<RequestLinkInfo, string>({
      query: (token) => `api/portal/requests/link/${encodeURIComponent(token)}`,
    }),
    getMyRequests: build.query<ClientRequest[], void>({
      query: () => "api/portal/requests",
      providesTags: ["PortalRequests"],
    }),
    getPortalRequest: build.query<ClientRequest, string>({
      query: (id) => `api/portal/requests/${id}`,
      providesTags: ["PortalRequests"],
    }),
    removePortalRequestFile: build.mutation<
      ClientRequest,
      { id: string; itemId: string; documentId: string }
    >({
      query: ({ id, itemId, documentId }) => ({
        url: `api/portal/requests/${id}/items/${itemId}/files/${documentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PortalRequests", "PortalFiles"],
    }),
    submitPortalRequest: build.mutation<
      ClientRequest,
      { id: string; answers: RequestAnswer[] }
    >({
      query: ({ id, answers }) => ({
        url: `api/portal/requests/${id}/submit`,
        method: "POST",
        body: { answers },
      }),
      invalidatesTags: ["PortalRequests"],
    }),
  }),
});

export const {
  useGetRequestLinkQuery,
  useGetMyRequestsQuery,
  useGetPortalRequestQuery,
  useRemovePortalRequestFileMutation,
  useSubmitPortalRequestMutation,
} = portalRequestsApi;
